import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import { podcastsApi } from "../api/podcastsApi";
import { useAuth } from "../auth/authStore";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import MobileQuickNav from "../components/layout/MobileQuickNav";
import { useToast } from "../ui/toast/ToastProvider";
import { usePodcastPlayer } from "../podcasts/playerStore";
import { isAegisEmbedUrl, resolveAegisPlayerUrl } from "../podcasts/aegisPlayer";
import { getSiteOrigin, joinUrl } from "../utils/seo";
import { openExternalUrl } from "../utils/safeUrl";
import { buildPsychologistProfilePath } from "../utils/psychologistProfile";

const PAGE_SIZE = 12;

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function formatNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return new Intl.NumberFormat("ru-RU").format(n);
}

function formatNumberCompact(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return formatNumber(n);
}

function formatEpisodeCount(value) {
  const n = Number(value) || 0;
  const mod10 = n % 10;
  const mod100 = n % 100;
  let suffix = "эпизодов";
  if (mod10 === 1 && mod100 !== 11) suffix = "эпизод";
  else if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) suffix = "эпизода";
  return `${formatNumber(n)} ${suffix}`;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatDuration(seconds) {
  const sec = Number(seconds);
  if (!Number.isFinite(sec) || sec <= 0) return "—";
  const m = Math.round(sec / 60);
  return `${m} мин`;
}

function resolveCatalogError(err) {
  const fallback = "Каталог подкастов временно недоступен. Попробуйте обновить страницу чуть позже.";
  const status = Number(err?.status);
  const message = String(err?.message || err?.error || "").trim();
  if (status >= 500) return fallback;
  if (!message) return fallback;
  const lowered = message.toLowerCase();
  if (lowered.includes("database error") || lowered.includes("internal server error")) return fallback;
  return message;
}

function initials(name) {
  const parsed = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk[0])
    .join("");
  return parsed ? parsed.toUpperCase() : "BS";
}

function normalizePodcast(item = {}) {
  const raw = asObject(item);
  const author = asObject(raw.author);
  return {
    id: raw.id ?? null,
    slug: raw.slug || "",
    title: String(raw.title || ""),
    shortDescription: String(raw.shortDescription || raw.description || ""),
    coverEmoji: String(raw.coverEmoji || ""),
    authorName: String(author.name || ""),
    authorAvatar: String(author.avatarUrl || ""),
    authorUserId: author.userId ?? author.id ?? raw.authorUserId ?? null,
    episodesCount: Number(raw.episodesCount || 0),
    listensCount: Number(raw.totalListens || raw.listensCount || raw.playsCount || 0),
    subscribed: Boolean(raw.subscribed),
    topic: String(raw.topic || asArray(raw.topics)[0] || ""),
    coverImageUrl: String(raw.coverImageUrl || raw.imageUrl || raw.coverUrl || raw.artworkUrl || ""),
  };
}

function normalizeEpisode(item = {}) {
  const raw = asObject(item);
  const author = asObject(raw.author);
  return {
    id: raw.id ?? null,
    podcastId: raw.podcastId ?? null,
    podcastSlug: String(raw.podcastSlug || ""),
    podcastTitle: String(raw.podcastTitle || ""),
    podcastCoverEmoji: String(raw.podcastCoverEmoji || ""),
    authorName: String(author.name || ""),
    authorUserId: author.userId ?? author.id ?? raw.authorUserId ?? null,
    episodeNumber: Number(raw.episodeNumber || 0),
    title: String(raw.title || ""),
    description: String(raw.description || ""),
    durationSeconds: Number(raw.durationSeconds || 0),
    durationLabel: formatDuration(raw.durationSeconds),
    publishedAt: raw.publishedAt || null,
    publishedLabel: formatDate(raw.publishedAt),
    listensCount: Number(raw.listensCount || 0),
    likesCount: Number(raw.likesCount || raw.reactionsTotal || raw.reactionsCount || raw.likeCount || 0),
    tags: asArray(raw.tags),
    featured: Boolean(raw.featured),
    saved: Boolean(raw.saved),
    playbackUrl: String(raw.playbackUrl || ""),
    externalUrl: String(raw.externalUrl || ""),
    aegisAssetId: String(raw.aegisAssetId || raw.assetId || raw.aegis_asset_id || raw.asset_id || ""),
    aegisProjectId: String(raw.aegisProjectId || raw.projectId || raw.aegis_project_id || raw.project_id || ""),
    aegisPlayerUrl: resolveAegisPlayerUrl(raw),
    isAegisEmbed:
      Boolean(resolveAegisPlayerUrl(raw)) ||
      isAegisEmbedUrl(raw.playbackUrl || "") ||
      isAegisEmbedUrl(raw.externalUrl || ""),
    coverImageUrl: String(
      raw.coverImageUrl ||
        raw.imageUrl ||
        raw.coverUrl ||
        raw.podcastCoverUrl ||
        raw.podcastCoverImageUrl ||
        raw.podcastImageUrl ||
        ""
    ),
  };
}

function normalizeCatalog(raw = {}) {
  const data = asObject(raw);
  return {
    hero: asObject(data.hero),
    topics: normalizeTopics(asArray(data.topics)),
    featuredEpisodes: asArray(data.featuredEpisodes).map(normalizeEpisode),
    podcasts: asArray(data.podcasts).map(normalizePodcast),
    latestEpisodes: asArray(data.latestEpisodes).map(normalizeEpisode),
    page: Number(data.page) || 0,
    size: Number(data.size) || PAGE_SIZE,
    total: Number(data.total) || 0,
  };
}

function normalizeTopics(values = []) {
  const seen = new Set();
  const out = [];
  asArray(values).forEach((item) => {
    const text = String(item || "").trim();
    if (!text) return;
    const key = text.toLocaleLowerCase("ru-RU");
    if (seen.has(key)) return;
    seen.add(key);
    out.push(text);
  });
  return out;
}

function podcastRouteId(podcast) {
  if (!podcast) return "";
  return podcast.slug || podcast.id || "";
}

function episodeIdentity(episode) {
  if (!episode) return "";
  if (episode.id !== null && episode.id !== undefined) return `id:${episode.id}`;
  const composite = `${episode.podcastId || ""}:${episode.podcastSlug || ""}:${episode.title || ""}:${episode.publishedAt || ""}`;
  return composite.trim();
}

function topicColorClass(topic, index = 0) {
  const t = String(topic || "").toLowerCase();
  if (t.includes("трев")) return "is-mint";
  if (t.includes("отнош")) return "is-lav";
  if (t.includes("карьер")) return "is-amber";
  if (t.includes("трав")) return "is-sky";
  if (t.includes("само")) return "is-coral";
  const byIndex = ["is-mint", "is-lav", "is-amber", "is-sky", "is-coral"];
  return byIndex[index % byIndex.length];
}

function readSearchParams(search) {
  const sp = new URLSearchParams(String(search || ""));
  const q = String(sp.get("q") || "").trim();
  const topic = String(sp.get("topic") || "").trim();
  const pageRaw = Number(sp.get("page"));
  const page = Number.isFinite(pageRaw) && pageRaw >= 0 ? Math.floor(pageRaw) : 0;
  const viewMode = String(sp.get("view") || "").trim();
  return {
    q,
    topic,
    page,
    viewMode: viewMode === "list" ? "list" : viewMode === "cards" ? "cards" : "",
  };
}

function buildSearchParams({ q = "", topic = "", page = 0, viewMode = "" } = {}) {
  const sp = new URLSearchParams();
  const qq = String(q || "").trim();
  const tt = String(topic || "").trim();
  const pp = Number(page);
  if (qq) sp.set("q", qq);
  if (tt) sp.set("topic", tt);
  if (Number.isFinite(pp) && pp > 0) sp.set("page", String(Math.floor(pp)));
  if (viewMode === "list") sp.set("view", "list");
  return sp.toString();
}

export default function Podcasts() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { isAuthenticated, role, me } = useAuth();
  const player = usePodcastPlayer();
  const didInitFromUrlRef = React.useRef(false);

  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [searchInput, setSearchInput] = React.useState("");
  const [query, setQuery] = React.useState("");
  const [topic, setTopic] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [viewMode, setViewMode] = React.useState("cards");
  const [allTopics, setAllTopics] = React.useState([]);
  const [catalog, setCatalog] = React.useState(() =>
    normalizeCatalog({
      hero: {},
      topics: [],
      featuredEpisodes: [],
      podcasts: [],
      latestEpisodes: [],
      page: 0,
      size: PAGE_SIZE,
      total: 0,
    })
  );

  const canManagePodcasts = ["PSYCHOLOGIST", "ADMIN", "MODERATOR"].includes(String(role || "").replace(/^ROLE_/i, "").toUpperCase());
  const podcastQuickNavItems = React.useMemo(() => [
    { to: "/podcasts", label: "Каталог", icon: "▶" },
    canManagePodcasts ? { to: "/dashboard/podcasts", label: "Мои", icon: "▦" } : null,
    canManagePodcasts ? { to: "/dashboard/podcasts/new", label: "Создать", icon: "+", accent: "create" } : null,
  ].filter(Boolean), [canManagePodcasts]);
  const origin = React.useMemo(() => getSiteOrigin(), []);
  const canonical = React.useMemo(() => joinUrl(origin, "/podcasts"), [origin]);
  const openPsychologistProfile = React.useCallback(
    (event, userId) => {
      const path = buildPsychologistProfilePath(userId);
      if (!path) return;
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      navigate(path);
    },
    [navigate]
  );

  // Hydrate state only when URL changes. Avoid depending on local state,
  // otherwise typing can be reverted by stale search params.
  React.useEffect(() => {
    const parsed = readSearchParams(location.search);
    didInitFromUrlRef.current = true;
    setSearchInput(parsed.q);
    setQuery(parsed.q);
    setTopic(parsed.topic);
    setPage(parsed.page);
    if (parsed.viewMode) setViewMode(parsed.viewMode);
  }, [location.search]);

  React.useEffect(() => {
    const nextQuery = String(searchInput || "").trim();
    if (nextQuery === query) return undefined;
    const t = setTimeout(() => {
      setPage(0);
      setQuery(nextQuery);
    }, 340);
    return () => clearTimeout(t);
  }, [query, searchInput]);

  // Keep URL in sync with current filters/search (but don't spam history).
  React.useEffect(() => {
    if (!didInitFromUrlRef.current) return;
    const next = buildSearchParams({ q: query, topic, page, viewMode });
    const current = String(location.search || "").replace(/^\?/, "");
    if (next === current) return;
    try {
      // Some environments (e.g. embedded webviews) can throw on history.replaceState.
      navigate(`${location.pathname}${next ? `?${next}` : ""}`, { replace: true });
    } catch {
      // Hard fallback: avoid crashing the page; URL sync is optional.
    }
  }, [location.pathname, location.search, navigate, page, query, topic, viewMode]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    podcastsApi
      .getCatalog({
        q: query,
        topic,
        page,
        size: PAGE_SIZE,
      })
      .then((res) => {
        if (cancelled) return;
        setCatalog(normalizeCatalog(res));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(resolveCatalogError(err));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [query, topic, page]);

  // Keep topic chips stable: load chips from the same query without topic filter.
  React.useEffect(() => {
    let cancelled = false;
    podcastsApi
      .getCatalog({
        q: query,
        topic: "",
        page: 0,
        size: PAGE_SIZE,
      })
      .then((res) => {
        if (cancelled) return;
        const normalized = normalizeCatalog(res);
        setAllTopics(normalized.topics);
      })
      .catch(() => {
        if (cancelled) return;
        setAllTopics([]);
      });

    return () => {
      cancelled = true;
    };
  }, [query]);

  const totalPages = Math.max(1, Math.ceil((Number(catalog.total) || 0) / (Number(catalog.size) || PAGE_SIZE)));
  const pageNumbers = React.useMemo(() => {
    const radius = 2;
    const start = Math.max(0, page - radius);
    const end = Math.min(totalPages - 1, page + radius);
    const result = [];
    for (let i = start; i <= end; i += 1) result.push(i);
    return result;
  }, [page, totalPages]);

  const featured = catalog.featuredEpisodes[0] || catalog.latestEpisodes[0] || null;
  const trendingEpisodes = React.useMemo(() => {
    const merged = [...catalog.featuredEpisodes, ...catalog.latestEpisodes];
    const unique = [];
    const seen = new Set();
    merged.forEach((episode) => {
      const key = episodeIdentity(episode);
      if (!key || seen.has(key)) return;
      seen.add(key);
      unique.push(episode);
    });
    unique.sort((a, b) => Number(b?.listensCount || 0) - Number(a?.listensCount || 0));
    return unique.slice(0, 6);
  }, [catalog.featuredEpisodes, catalog.latestEpisodes]);

  const openPodcast = (podcast) => {
    const rid = podcastRouteId(podcast);
    if (!rid) return;
    navigate(`/podcasts/${encodeURIComponent(String(rid))}`);
  };

  const openEpisode = React.useCallback((episode) => {
    if (!episode) return;
    if (episode.id !== null && episode.id !== undefined) {
      navigate(`/podcasts/episodes/${encodeURIComponent(String(episode.id))}`);
      return;
    }
    const rid = episode.podcastSlug || episode.podcastId;
    if (rid) {
      navigate(`/podcasts/${encodeURIComponent(String(rid))}`);
      return;
    }
    const fallbackUrl = episode.externalUrl || episode.playbackUrl;
    if (fallbackUrl && !openExternalUrl(fallbackUrl)) {
      toast.error("Некорректная ссылка эпизода");
    }
  }, [navigate, toast]);

  const playEpisodeInline = React.useCallback(async (episode) => {
    if (!episode) return;
    const playbackQueue =
      Array.isArray(catalog.latestEpisodes) && catalog.latestEpisodes.length
        ? catalog.latestEpisodes
        : [episode];
    const queueStartIndex = playbackQueue.findIndex((item) => String(item?.id ?? "") === String(episode?.id ?? ""));
    const ok = await player.playTrack(
      {
        id: episode.id,
        episodeId: episode.id,
        podcastId: episode.podcastId,
        podcastSlug: episode.podcastSlug,
        title: episode.title,
        podcastTitle: episode.podcastTitle,
        coverImageUrl: episode.coverImageUrl,
        durationSeconds: episode.durationSeconds,
        playbackUrl: episode.playbackUrl || episode.externalUrl,
        assetId: episode.aegisAssetId,
        projectId: episode.aegisProjectId,
        isAegisEmbed: episode.isAegisEmbed,
      },
      {
        autoplay: true,
        queue: playbackQueue,
        queueStartIndex: queueStartIndex >= 0 ? queueStartIndex : 0,
      }
    );
    if (!ok) {
      openEpisode(episode);
      return;
    }
    if (episode.id) {
      podcastsApi.registerListen(episode.id)
        .then((listen) => {
          const nextEpisodeListens = Number(listen?.episodeListens);
          const nextPodcastListens = Number(listen?.podcastListens);
          if (!Number.isFinite(nextEpisodeListens)) return;
          setCatalog((prev) => ({
            ...prev,
            featuredEpisodes: prev.featuredEpisodes.map((item) =>
              String(item.id) === String(episode.id)
                ? { ...item, listensCount: nextEpisodeListens }
                : item
            ),
            latestEpisodes: prev.latestEpisodes.map((item) =>
              String(item.id) === String(episode.id)
                ? { ...item, listensCount: nextEpisodeListens }
                : item
            ),
            podcasts: Number.isFinite(nextPodcastListens)
              ? prev.podcasts.map((item) =>
                  String(item.id) === String(episode.podcastId)
                    ? { ...item, listensCount: nextPodcastListens }
                    : item
                )
              : prev.podcasts,
          }));
        })
        .catch(() => {});
    }
  }, [catalog.latestEpisodes, openEpisode, player]);

  const activeEpisodeId = React.useMemo(
    () => String(player.track?.episodeId ?? player.track?.id ?? ""),
    [player.track?.episodeId, player.track?.id]
  );

  const isEpisodeActive = React.useCallback(
    (episode) => Boolean(episode?.id) && String(episode.id) === activeEpisodeId,
    [activeEpisodeId]
  );

  const handleEpisodePlayClick = React.useCallback(
    (event, episode) => {
      event.stopPropagation();
      if (!episode) return;
      if (isEpisodeActive(episode)) {
        player.togglePlay();
        return;
      }
      void playEpisodeInline(episode);
    },
    [isEpisodeActive, player, playEpisodeInline]
  );

  const setTopicFilter = (nextTopic) => {
    setPage(0);
    setTopic(String(nextTopic || "").trim());
  };

  const toggleSubscribe = async (event, podcast) => {
    event.stopPropagation();
    if (!podcast?.id) return;
    if (!isAuthenticated) {
      toast.info("Подписка доступна после авторизации");
      navigate("/login");
      return;
    }
    try {
      if (podcast.subscribed) await podcastsApi.unsubscribe(podcast.id);
      else await podcastsApi.subscribe(podcast.id);
      setCatalog((prev) => ({
        ...prev,
        podcasts: prev.podcasts.map((item) =>
          item.id === podcast.id ? { ...item, subscribed: !item.subscribed } : item
        ),
      }));
      toast.success(podcast.subscribed ? "Подписка отменена" : "Подписка оформлена");
    } catch (err) {
      toast.error(err?.message || "Не удалось изменить подписку");
    }
  };

  const toggleSaveEpisode = async (event, episode) => {
    event.stopPropagation();
    if (!episode?.id) return;
    if (!isAuthenticated) {
      toast.info("Сохранение доступно после авторизации");
      navigate("/login");
      return;
    }
    try {
      if (episode.saved) await podcastsApi.unsaveEpisode(episode.id);
      else await podcastsApi.saveEpisode(episode.id);
      setCatalog((prev) => ({
        ...prev,
        latestEpisodes: prev.latestEpisodes.map((item) =>
          item.id === episode.id ? { ...item, saved: !item.saved } : item
        ),
        featuredEpisodes: prev.featuredEpisodes.map((item) =>
          item.id === episode.id ? { ...item, saved: !item.saved } : item
        ),
      }));
      toast.success(episode.saved ? "Убрано из сохранённого" : "Эпизод сохранён");
    } catch (err) {
      toast.error(err?.message || "Не удалось сохранить эпизод");
    }
  };

  const addEpisodeToPlaylist = React.useCallback((event, episode) => {
    event.stopPropagation();
    if (!episode) return;
    const added = player.enqueueTrack({
      id: episode.id,
      episodeId: episode.id,
      podcastId: episode.podcastId,
      podcastSlug: episode.podcastSlug,
      title: episode.title,
      podcastTitle: episode.podcastTitle,
      coverImageUrl: episode.coverImageUrl,
      durationSeconds: episode.durationSeconds,
      playbackUrl: episode.playbackUrl || episode.externalUrl,
      assetId: episode.aegisAssetId,
      projectId: episode.aegisProjectId,
      isAegisEmbed: episode.isAegisEmbed,
    });
    if (added) {
      toast.success("Эпизод добавлен в плейлист");
    } else {
      toast.info("Этот эпизод уже в плейлисте");
    }
  }, [player, toast]);

  const goToMyPodcasts = () => {
    if (!canManagePodcasts) {
      toast.info("Раздел доступен психологам, модераторам и администраторам");
      return;
    }
    navigate("/dashboard/podcasts");
  };

  return (
    <div className="podcasts-hub">
      <Helmet prioritizeSeoTags>
        <title>Подкасты | Бюро Души</title>
        <meta
          name="description"
          content="Подкасты психологов платформы: тревога, отношения, самопознание, карьера и другие темы."
        />
        {canonical ? <link rel="canonical" href={canonical} /> : null}
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="podcasts-shell">
        <AppSidebarV2
          activeKey="podcasts"
          mobileOpen={sidebarOpen}
          onCloseMobile={() => setSidebarOpen(false)}
        />

        <button
          className={`podcasts-overlay ${sidebarOpen ? "is-open" : ""}`}
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setSidebarOpen(false)}
        />

        <div className="podcasts-main">
          <header className="podcasts-topbar">
            <div className="podcasts-topbar__left">
              <button
                className="podcasts-topbar__menu-btn"
                type="button"
                onClick={() => setSidebarOpen(true)}
                aria-label="Открыть меню"
              >
                ☰
              </button>
              <div className="podcasts-topbar__search">
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="8.5" cy="8.5" r="5.5" />
                  <path d="M13 13l4 4" strokeLinecap="round" />
                </svg>
                <input
                  type="text"
                  placeholder="Найти подкаст по названию..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            </div>
            <div className="podcasts-topbar__right">
              {canManagePodcasts ? (
                <button className="podcasts-btn podcasts-btn--mint" type="button" onClick={goToMyPodcasts}>
                  <span>Мои подкасты</span>
                </button>
              ) : null}
              <div className="podcasts-topbar__separator" />
              <button className="podcasts-topbar__avatar" type="button" onClick={() => navigate("/dashboard/settings")}>
                {initials(me?.name || me?.login)}
              </button>
            </div>
          </header>

          <main className="podcasts-content">
            <div className="podcasts-page-header">
              <div>
                <h1 className="podcasts-page-title">Подкасты</h1>
                <p className="podcasts-page-sub">Психологические беседы от практикующих специалистов</p>
              </div>
              <div className="podcasts-view-switcher" role="tablist" aria-label="Режим отображения">
                <button
                  type="button"
                  className={`podcasts-view-btn ${viewMode === "cards" ? "active" : ""}`}
                  onClick={() => setViewMode("cards")}
                  role="tab"
                  aria-selected={viewMode === "cards"}
                >
                  Карточки
                </button>
                <button
                  type="button"
                  className={`podcasts-view-btn ${viewMode === "list" ? "active" : ""}`}
                  onClick={() => setViewMode("list")}
                  role="tab"
                  aria-selected={viewMode === "list"}
                >
                  Список
                </button>
              </div>
            </div>

            <div className="podcasts-filters-row">
              <button
                type="button"
                className={`podcasts-filter-chip ${topic ? "" : "active"}`}
                onClick={() => setTopicFilter("")}
              >
                Все подкасты
              </button>
              {(allTopics.length ? allTopics : catalog.topics).map((topicValue, index) => {
                const active = String(topic || "").toLowerCase() === String(topicValue || "").toLowerCase();
                return (
                  <button
                    type="button"
                    key={topicValue}
                    className={`podcasts-filter-chip ${active ? "active" : ""}`}
                    onClick={() => setTopicFilter(topicValue)}
                  >
                    <span className={`podcasts-filter-dot ${topicColorClass(topicValue, index)}`} />
                    {topicValue}
                  </button>
                );
              })}
            </div>

            {trendingEpisodes.length > 0 ? (
              <>
                <div className="podcasts-section-label">Сейчас слушают</div>
                <section className="podcasts-trending-strip">
                  {trendingEpisodes.map((episode, index) => (
                    <article
                      key={episode.id || `trending-${index}`}
                      className="podcasts-trending-pill"
                      role="button"
                      tabIndex={0}
                      onClick={() => openEpisode(episode)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openEpisode(episode);
                        }
                      }}
                    >
                      <div className="podcasts-trending-cover">
                        {episode.coverImageUrl ? (
                          <img src={episode.coverImageUrl} alt={episode.podcastTitle} loading="lazy" />
                        ) : (
                          <span>{episode.podcastCoverEmoji || "🎙️"}</span>
                        )}
                      </div>
                      <div className="podcasts-trending-info">
                        {episode.podcastTitle ? (
                          <div className="podcasts-trending-name">{episode.podcastTitle}</div>
                        ) : null}
                        <div className="podcasts-trending-meta">
                          Эп. {episode.episodeNumber || index + 1} · {episode.durationLabel}
                        </div>
                      </div>
                      <div className="podcasts-trending-listeners">
                        {formatNumberCompact(episode.listensCount)} ▶
                      </div>
                    </article>
                  ))}
                </section>
              </>
            ) : null}

            {featured ? (
              <>
                <div className="podcasts-section-label">Рекомендуем</div>
                <section className="featured-podcast" role="button" tabIndex={0} onClick={() => openEpisode(featured)} onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openEpisode(featured);
                  }
                }}>
                  <div className="featured-cover">
                    {featured.coverImageUrl ? (
                      <img src={featured.coverImageUrl} alt={featured.podcastTitle} loading="lazy" />
                    ) : (
                      <div className="featured-cover__placeholder">{featured.podcastCoverEmoji || "🎙️"}</div>
                    )}
                    <div className="featured-cover-overlay" />
                  </div>
                  <div className="featured-content">
                    <div>
                      <div className="featured-badge">Новые эпизоды</div>
                      <div className="featured-title">{featured.podcastTitle}</div>
                      {featured.description ? (
                        <div className="featured-desc">{featured.description}</div>
                      ) : null}
                      <div className="featured-meta">
                        <div className="featured-author">
                          {featured.authorUserId ? (
                            <button
                              type="button"
                              className="b-psych-link-btn"
                              onClick={(event) => openPsychologistProfile(event, featured.authorUserId)}
                            >
                              <div className="featured-author-avatar">{initials(featured.authorName)}</div>
                              <div className="featured-author-name">{featured.authorName}</div>
                            </button>
                          ) : (
                            <>
                              <div className="featured-author-avatar">{initials(featured.authorName)}</div>
                              <div className="featured-author-name">{featured.authorName}</div>
                            </>
                          )}
                        </div>
                        <div className="featured-stat">· {featured.durationLabel}</div>
                        <div className="featured-stat">· {featured.publishedLabel}</div>
                      </div>
                    </div>
                    <div className="featured-actions">
                      <button
                        className="podcasts-btn podcasts-btn--play"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          playEpisodeInline(featured);
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M5 3l9 5-9 5V3z" />
                        </svg>
                        Слушать
                      </button>
                      <button
                        className="podcasts-btn podcasts-btn--subtle"
                        type="button"
                        onClick={(e) => toggleSaveEpisode(e, featured)}
                      >
                        {featured.saved ? "Сохранено" : "Сохранить"}
                      </button>
                    </div>
                  </div>
                </section>
              </>
            ) : null}

            <div className="podcasts-section-label">Все подкасты</div>

            {loading ? <div className="podcasts-empty">Загрузка подкастов…</div> : null}
            {!loading && error ? <div className="podcasts-empty">{error}</div> : null}

            {!loading && !error && catalog.podcasts.length === 0 ? (
              <div className="podcasts-empty">
                {query || topic
                  ? "Ничего не найдено по вашему запросу."
                  : "В каталоге пока нет подкастов."}
              </div>
            ) : null}

            {!loading && !error && catalog.podcasts.length > 0 ? (
              <section className={`podcast-grid ${viewMode === "list" ? "is-list" : ""}`}>
                {catalog.podcasts.map((podcast, index) => (
                  <article
                    key={podcast.id || `pod-${index}`}
                    className="podcast-card"
                    onClick={() => openPodcast(podcast)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openPodcast(podcast);
                      }
                    }}
                  >
                    <div className="podcast-card-cover">
                      {podcast.coverImageUrl ? (
                        <img className="podcast-card-cover__img" src={podcast.coverImageUrl} alt={podcast.title} loading="lazy" />
                      ) : (
                        <div className={`podcast-card-cover-emoji ${topicColorClass(podcast.topic, index)}`}>{podcast.coverEmoji || "🎙️"}</div>
                      )}
                      <div className="podcast-card-play-overlay">
                        <div className="podcast-card-play-btn">
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M5 3l9 5-9 5V3z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div className="podcast-card-body">
                      {podcast.topic ? <div className={`podcast-card-tag ${topicColorClass(podcast.topic, index)}`}>{podcast.topic}</div> : null}
                      {podcast.title ? <h3 className="podcast-card-title">{podcast.title}</h3> : null}
                      {podcast.authorName ? (
                        <div className="podcast-card-author">
                          {podcast.authorUserId ? (
                            <button
                              type="button"
                              className="b-psych-link-btn"
                              onClick={(event) => openPsychologistProfile(event, podcast.authorUserId)}
                            >
                              {podcast.authorName}
                            </button>
                          ) : (
                            podcast.authorName
                          )}
                        </div>
                      ) : null}
                      <div className="podcast-card-footer">
                        <div className="podcast-card-eps">{formatEpisodeCount(podcast.episodesCount)}</div>
                        <button
                          type="button"
                          className={`podcast-card-sub-btn ${podcast.subscribed ? "subscribed" : ""}`}
                          onClick={(e) => toggleSubscribe(e, podcast)}
                        >
                          {podcast.subscribed ? "✓ Подписан" : "Подписаться"}
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </section>
            ) : null}

            <section className="episodes-section">
              <div className="episodes-header">
                <div className="podcasts-section-label">Новые эпизоды</div>
              </div>
              <div className="episodes-list">
                {catalog.latestEpisodes.length === 0 ? (
                  <div className="podcasts-empty podcasts-empty--inline">Эпизоды пока не опубликованы</div>
                ) : (
                  catalog.latestEpisodes.slice(0, 10).map((episode, index) => (
                    <article
                      className={`episode-item ${isEpisodeActive(episode) ? "is-active" : ""}`}
                      key={episode.id || `ep-${index}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => openEpisode(episode)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openEpisode(episode);
                        }
                      }}
                    >
                      <div className="episode-num">{episode.episodeNumber || index + 1}</div>
                      <div className="episode-thumb">
                        {episode.coverImageUrl ? (
                          <img src={episode.coverImageUrl} alt={episode.podcastTitle} loading="lazy" />
                        ) : (
                          <span>{episode.podcastCoverEmoji || "🎙️"}</span>
                        )}
                      </div>
                      <div className="episode-info">
                        {episode.title ? <div className="episode-title">{episode.title}</div> : null}
                        <div className="episode-meta">
                          {episode.podcastTitle ? <span>{episode.podcastTitle}</span> : null}
                          <span className="episode-dot" />
                          <span>{episode.publishedLabel}</span>
                          <span className="episode-dot" />
                          <span>{formatNumberCompact(episode.listensCount)} прослуш.</span>
                          <span className="episode-dot" />
                          <span className="episode-likes">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                            </svg>
                            {formatNumberCompact(episode.likesCount)}
                          </span>
                        </div>
                      </div>
                      <div className="episode-actions">
                        <button
                          type="button"
                          className={`ep-icon-btn ep-fav-btn ${episode.saved ? "saved" : ""}`}
                          onClick={(e) => toggleSaveEpisode(e, episode)}
                          title={episode.saved ? "Убрать из избранного" : "Добавить в избранное"}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="ep-icon-btn ep-queue-btn"
                          onClick={(e) => addEpisodeToPlaylist(e, episode)}
                          title="Добавить в плейлист"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M4 6h12M4 12h12M4 18h8" strokeLinecap="round" />
                            <path d="M19 11v8M15 15h8" strokeLinecap="round" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className={`ep-play-btn ${isEpisodeActive(episode) ? "is-playing" : ""}`}
                          onClick={(e) => handleEpisodePlayClick(e, episode)}
                          title={isEpisodeActive(episode) ? (player.isPlaying ? "Пауза" : "Продолжить") : "Слушать"}
                        >
                          {isEpisodeActive(episode) && player.isPlaying ? (
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="#fff">
                              <rect x="4" y="3" width="3" height="10" rx="1" />
                              <rect x="9" y="3" width="3" height="10" rx="1" />
                            </svg>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="#fff">
                              <path d="M5 3l9 5-9 5V3z" />
                            </svg>
                          )}
                        </button>
                        <div className="ep-duration">{episode.durationLabel}</div>
                      </div>
                    </article>
                  ))
                )}
              </div>
            </section>

            {!loading && !error && totalPages > 1 ? (
              <div className="pagination">
                <button
                  type="button"
                  className="page-btn"
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  disabled={page <= 0}
                >
                  ←
                </button>
                {pageNumbers.map((pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    className={`page-btn ${pageNumber === page ? "active" : ""}`}
                    onClick={() => setPage(pageNumber)}
                  >
                    {pageNumber + 1}
                  </button>
                ))}
                <button
                  type="button"
                  className="page-btn"
                  onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                  disabled={page >= totalPages - 1}
                >
                  →
                </button>
              </div>
            ) : null}
          </main>
          <MobileQuickNav items={podcastQuickNavItems} />
        </div>
      </div>
    </div>
  );
}
